<template>
  <div class="player-status-bar" :class="{ 'has-effects': hasPlayerStatusEffects }">
    <!-- Toggle Button -->
    <button 
      class="status-toggle-btn"
      @click="toggleExpanded"
      :title="expanded ? 'Fechar Status' : 'Ver Status Effects'">
      <div class="status-indicator">
        <i class="material-icons">{{ expanded ? 'close' : 'psychology' }}</i>
        <div v-if="playerStatusSummary.total > 0" class="status-count">
          {{ playerStatusSummary.total }}
        </div>
      </div>
    </button>

    <!-- Expanded Status Panel -->
    <transition name="slide-right">
      <div v-if="expanded" class="status-panel">
        
        <!-- Header -->
        <div class="panel-header">
          <h3>
            <i class="material-icons">psychology</i>
            Status Effects
          </h3>
          <div class="header-stats">
            <span class="buffs-count">
              <i class="material-icons">trending_up</i>
              {{ playerStatusSummary.buffs }}
            </span>
            <span class="debuffs-count">
              <i class="material-icons">trending_down</i>
              {{ playerStatusSummary.debuffs }}
            </span>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="$store.state.statusEffects.loading.playerBuffs || $store.state.statusEffects.loading.playerDebuffs" 
             class="loading-status">
          <div class="loading-spinner"></div>
          <span>Carregando status...</span>
        </div>

        <!-- No Effects State -->
        <div v-else-if="!hasPlayerStatusEffects" class="no-effects">
          <i class="material-icons">sentiment_satisfied</i>
          <p>Nenhum efeito ativo</p>
          <small>Você está livre de buffs e debuffs</small>
        </div>

        <!-- Effects List -->
        <div v-else class="effects-list">
          
          <!-- Active Buffs -->
          <div v-if="activePlayerBuffs.length > 0" class="effects-section">
            <h4 class="section-title buffs">
              <i class="material-icons">trending_up</i>
              Buffs ({{ activePlayerBuffs.length }})
            </h4>
            
            <div class="effects-grid">
              <div 
                v-for="buff in activePlayerBuffs" 
                :key="buff.id"
                class="effect-item buff"
                @mouseenter="showTooltip($event, buff, false)"
                @mouseleave="hideTooltip">
                
                <div class="effect-icon" :style="{ color: getStatusEffectColor(buff.effectType, false) }">
                  <i class="material-icons">{{ getStatusEffectIcon(buff.effectType, false) }}</i>
                  
                  <!-- Timer Ring -->
                  <div class="timer-ring">
                    <svg class="timer-circle" width="36" height="36">
                      <circle 
                        cx="18" 
                        cy="18" 
                        r="16"
                        :style="{ 
                          strokeDasharray: `${getTimeProgress(buff)} 100`,
                          stroke: getStatusEffectColor(buff.effectType, false)
                        }"
                      />
                    </svg>
                  </div>
                </div>
                
                <div class="effect-info">
                  <span class="effect-name">{{ getStatusEffectName(buff.effectType, false) }}</span>
                  <span class="effect-value positive">{{ formatEffectValue(buff.effectValue) }}</span>
                  <span class="effect-time" :class="{ 'expiring': isExpiringSoon(buff.expiresAt) }">
                    {{ formatTimeRemaining(buff.expiresAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Debuffs -->
          <div v-if="activePlayerDebuffs.length > 0" class="effects-section">
            <h4 class="section-title debuffs">
              <i class="material-icons">trending_down</i>
              Debuffs ({{ activePlayerDebuffs.length }})
            </h4>
            
            <div class="effects-grid">
              <div 
                v-for="debuff in activePlayerDebuffs" 
                :key="debuff.id"
                class="effect-item debuff"
                @mouseenter="showTooltip($event, debuff, true)"
                @mouseleave="hideTooltip"
                @click="considerCureDebuff(debuff)">
                
                <div class="effect-icon" :style="{ color: getStatusEffectColor(debuff.effectType, true) }">
                  <i class="material-icons">{{ getStatusEffectIcon(debuff.effectType, true) }}</i>
                  
                  <!-- Timer Ring -->
                  <div class="timer-ring">
                    <svg class="timer-circle" width="36" height="36">
                      <circle 
                        cx="18" 
                        cy="18" 
                        r="16"
                        :style="{ 
                          strokeDasharray: `${getTimeProgress(debuff)} 100`,
                          stroke: getStatusEffectColor(debuff.effectType, true)
                        }"
                      />
                    </svg>
                  </div>
                </div>
                
                <div class="effect-info">
                  <span class="effect-name">{{ getStatusEffectName(debuff.effectType, true) }}</span>
                  <span class="effect-value negative">{{ formatEffectValue(debuff.effectValue) }}</span>
                  <span class="effect-time" :class="{ 'expiring': isExpiringSoon(debuff.expiresAt) }">
                    {{ formatTimeRemaining(debuff.expiresAt) }}
                  </span>
                  
                  <!-- Cure Button -->
                  <button v-if="debuff.curable" class="cure-btn" title="Curar Debuff">
                    <i class="material-icons">healing</i>
                    {{ debuff.cureCost }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="panel-footer">
          <button class="refresh-btn" @click="refreshStatusEffects" :disabled="isRefreshing">
            <i class="material-icons" :class="{ 'spinning': isRefreshing }">refresh</i>
            Atualizar
          </button>
          
          <button class="hospital-btn" @click="goToHospital">
            <i class="material-icons">local_hospital</i>
            Hospital
          </button>
        </div>
      </div>
    </transition>

    <!-- Status Tooltip -->
    <StatusTooltip 
      :visible="tooltip.visible"
      :status-effect="tooltip.effect"
      :position="tooltip.position"
      :is-debuff="tooltip.isDebuff"
    />
  </div>
</template>

<script>
import StatusTooltip from './StatusTooltip.vue';
import statusEffectsMixin from '../mixins/statusEffectsMixin';

export default {
  name: 'PlayerStatusBar',
  components: {
    StatusTooltip
  },
  mixins: [statusEffectsMixin],

  data() {
    return {
      expanded: false,
      isRefreshing: false,
      tooltip: {
        visible: false,
        effect: null,
        position: { x: 0, y: 0 },
        isDebuff: false
      }
    };
  },

  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
      
      if (this.expanded) {
        // Refresh data when opening
        this.refreshStatusEffects();
      }
    },

    async refreshStatusEffects() {
      this.isRefreshing = true;
      try {
        await this.loadPlayerStatusEffects();
      } finally {
        this.isRefreshing = false;
      }
    },

    showTooltip(event, effect, isDebuff) {
      const rect = event.target.closest('.effect-item').getBoundingClientRect();
      
      this.tooltip = {
        visible: true,
        effect: effect,
        position: {
          x: rect.right + 10,
          y: rect.top
        },
        isDebuff: isDebuff
      };
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },

    getTimeProgress(effect) {
      if (!effect) return 0;
      
      const now = new Date().getTime();
      const createdAt = new Date(effect.createdAt).getTime();
      const expiresAt = new Date(effect.expiresAt).getTime();
      
      const totalDuration = expiresAt - createdAt;
      const remaining = Math.max(0, expiresAt - now);
      
      return Math.min(100, (remaining / totalDuration) * 100);
    },

    async considerCureDebuff(debuff) {
      if (!debuff.curable) return;
      
      const playerCoins = this.$store.state.player.coins || 0;
      
      if (playerCoins < debuff.cureCost) {
        this.$notify({
          type: 'warning',
          title: 'Coins Insuficientes',
          text: `Você precisa de ${debuff.cureCost} coins para curar este debuff`
        });
        return;
      }

      // Confirm cure
      const confirmed = confirm(
        `Curar "${this.getStatusEffectName(debuff.effectType, true)}" por ${debuff.cureCost} coins?`
      );
      
      if (confirmed) {
        try {
          await this.curePlayerDebuffWithFeedback(debuff.id);
        } catch (error) {
          // Error handling is done in mixin
        }
      }
    },

    goToHospital() {
      this.$router.push('/hospital');
      this.expanded = false;
    }
  },

  // Auto-close when clicking outside
  mounted() {
    document.addEventListener('click', (event) => {
      if (!this.$el.contains(event.target)) {
        this.expanded = false;
      }
    });
  }
};
</script>

<style lang="scss" scoped>
@import '../assets/styles/colors.scss';

.player-status-bar {
  position: fixed;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1000;
  font-family: 'Rajdhani', sans-serif;
}

.status-toggle-btn {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(52, 152, 219, 0.8), rgba(52, 152, 219, 0.6));
  border: 2px solid $primary-blue;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);

  &:hover {
    transform: scale(1.05);
    background: linear-gradient(145deg, rgba(52, 152, 219, 1), rgba(52, 152, 219, 0.8));
    box-shadow: 0 0 20px rgba(52, 152, 219, 0.5);
  }

  .status-indicator {
    position: relative;

    .material-icons {
      color: white;
      font-size: 24px;
      text-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
    }

    .status-count {
      position: absolute;
      top: -8px;
      right: -8px;
      background: $danger;
      color: white;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: bold;
      border: 2px solid white;
      animation: pulse 2s infinite;
    }
  }
}

.status-panel {
  position: absolute;
  left: 70px;
  top: -200px;
  width: 320px;
  height: 400px;
  background: linear-gradient(145deg, rgba(45, 45, 45, 0.95), rgba(26, 26, 26, 0.98));
  border: 2px solid $primary-blue;
  border-radius: 12px;
  backdrop-filter: blur(15px);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, $primary-blue, transparent);
    animation: scanline 2s linear infinite;
  }
}

.panel-header {
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    color: $text-primary;
    font-size: 18px;
    display: flex;
    align-items: center;
    gap: 8px;

    .material-icons {
      color: $primary-blue;
    }
  }

  .header-stats {
    display: flex;
    gap: 12px;

    .buffs-count, .debuffs-count {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 14px;
      font-weight: 600;

      .material-icons {
        font-size: 16px;
      }
    }

    .buffs-count {
      color: $success;
    }

    .debuffs-count {
      color: $danger;
    }
  }
}

.loading-status {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: $text-secondary;

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(52, 152, 219, 0.3);
    border-top: 3px solid $primary-blue;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

.no-effects {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  color: $text-secondary;

  .material-icons {
    font-size: 48px;
    margin-bottom: 8px;
    color: $success;
  }

  p {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 600;
  }

  small {
    font-size: 12px;
    opacity: 0.7;
  }
}

.effects-list {
  flex: 1;
  padding: 16px;
  overflow-y: auto;

  .effects-section {
    margin-bottom: 20px;

    .section-title {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 6px;

      &.buffs {
        color: $success;
      }

      &.debuffs {
        color: $danger;
      }

      .material-icons {
        font-size: 16px;
      }
    }

    .effects-grid {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }
}

.effect-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(2px);
  }

  &.debuff {
    border-left: 3px solid $danger;
  }

  &.buff {
    border-left: 3px solid $success;
  }

  .effect-icon {
    position: relative;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;

    .material-icons {
      font-size: 20px;
      z-index: 2;
      position: relative;
    }

    .timer-ring {
      position: absolute;
      top: 0;
      left: 0;

      .timer-circle {
        transform: rotate(-90deg);
        
        circle {
          fill: none;
          stroke-width: 2;
          stroke-linecap: round;
          opacity: 0.7;
        }
      }
    }
  }

  .effect-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .effect-name {
      font-size: 13px;
      font-weight: 600;
      color: $text-primary;
    }

    .effect-value {
      font-size: 12px;
      font-weight: 600;

      &.positive {
        color: $success;
      }

      &.negative {
        color: $danger;
      }
    }

    .effect-time {
      font-size: 10px;
      color: $text-secondary;

      &.expiring {
        color: $danger;
        animation: pulse 1s infinite;
      }
    }

    .cure-btn {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      background: $danger;
      color: white;
      border: none;
      border-radius: 4px;
      padding: 4px 8px;
      font-size: 10px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: background 0.2s ease;

      &:hover {
        background: darken($danger, 10%);
      }

      .material-icons {
        font-size: 12px;
      }
    }
  }
}

.panel-footer {
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 8px;

  button {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.05);
    color: $text-primary;
    cursor: pointer;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .material-icons {
      font-size: 16px;

      &.spinning {
        animation: spin 1s linear infinite;
      }
    }
  }

  .refresh-btn {
    border-color: $primary-blue;
    color: $primary-blue;
  }

  .hospital-btn {
    border-color: $success;
    color: $success;
  }
}

// Transitions
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

// Animations
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes scanline {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
</style>